import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import {
  isMobile,
  isTablet,
  isIos,
  HealthMonitorCodes,
  SessionState,
} from "@biosensesignal/web-sdk";
import {
  useError,
  useLicenseKey,
  useMeasurementDuration,
  useMonitor,
  usePageVisibility,
  usePrevious,
  useWarning,
} from "../hooks";
import { mirror } from "../style/mirror";
import { Flex } from "./shared/Flex";
import media from "../style/media";
import { ErrorAlert, WarningAlert } from "./alert";
import Loader from "./Loader";
import { VideoReadyState } from "../types";
import Mask from "../assets/scan-green-new.svg";
import MaskError from "../assets/scan-red-new.svg";
import { useNavigate } from "react-router-dom";
import BottomInstructionModal from "./BottomInstructionModal";
import FaceScanModal from "./FaceScanModal";
import ResultPage from "./ResultPage";
import axios from "axios";
import { requestCameraPermission } from "../utils/permissions";

const MonitorWrapper = styled(Flex)<{ isSettingsOpen: boolean }>`
  flex-direction: column;
  width: 100%;
  height: 50vh;
  justify-content: start;
  align-items: center;
  flex: 1;
  z-index: ${({ isSettingsOpen }) => isSettingsOpen && "-1"};
  ${media.tablet`
    width: fit-content;
    justify-content: center;
  `}
`;

const MeasurementContentWrapper = styled(Flex)<{ isMobile: boolean }>`
  width: 100%;
  height: ${({ isMobile }) => (isMobile ? "100vh" : "auto")};
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  ${media.mobile`
    margin: 40px 0 60px 0;
  `}
`;

const VideoAndStatsWrapper = styled(Flex)<{ isMobile: boolean }>`
  position: relative;
  justify-content: center;
  width: 100vw;
  height: 75vh;
  overflow: hidden;
`;

const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  ${mirror};
`;

const Img = styled.img<{ isDesktop: boolean }>`
  position: absolute;
  width: ${({ isDesktop }) =>
    isDesktop ? "60%" : "100%"}; /* Stretch image width slightly for desktop */
  height: 213%;
  top: 57%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  object-fit: contain;
  object-position: center center;
`;

const RetryModal = ({ isOpen, onRetry }) => {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
        overflow: "hidden", // Prevents scrolling
      }}
    >
      <div
        style={{
          backgroundColor: "#fff",
          padding: "20px",
          borderRadius: "8px",
          textAlign: "center",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          maxWidth: "90%", // Prevents the content from being too wide
          width: "400px", // Centered width
          height: "100vh",
        }}
      >
        <div
          style={{
            marginBottom: "10px",
            position: "absolute",
            top: "33%",
            left: "18%",
          }}
        >
          <h2 style={{ fontSize: "26px" }}>Something went wrong</h2>
          <p style={{ fontSize: "24px" }}>Please scan again.</p>
        </div>

        <button
          onClick={onRetry}
          style={{
            marginTop: "10px",
            padding: "10px 20px",
            borderRadius: "4px",
            backgroundColor: "#007BFF",
            color: "#fff",
            border: "none",
            cursor: "pointer",
            position: "absolute",
            top: "44%",
            left: "48%",
            transform: "translate(-50%, -50%)",
            fontSize: "20px",
          }}
        >
          Retry
        </button>
      </div>
    </div>
  );
};

// Add a new component for permission errors
const PermissionErrorOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  text-align: center;
  color: white;
`;

const PermissionIcon = styled.div`
  font-size: 48px;
  margin-bottom: 20px;
`;

const PermissionTitle = styled.h2`
  font-size: 24px;
  margin-bottom: 16px;
`;

const PermissionMessage = styled.p`
  font-size: 16px;
  margin-bottom: 24px;
  max-width: 80%;
  line-height: 1.5;
`;

const RetryButton = styled.button`
  background-color: #007BFF;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  
  &:hover {
    background-color: #0069d9;
  }
`;

const BinahMonitor = ({
  showMonitor,
  cameraId,
  onLicenseStatus,
  isSettingsOpen,
}) => {
  if (!showMonitor) {
    return null;
  }

  const video = useRef<HTMLVideoElement>(null);
  const [isMeasurementEnabled, setIsMeasurementEnabled] =
    useState<boolean>(false);
  const [startMeasuring, setStartMeasuring] = useState<boolean>(false);
  const [isResultReady, setIsResultReady] = useState(false);
  const [loadingTimeoutPromise, setLoadingTimeoutPromise] = useState<number>();
  const isPageVisible = usePageVisibility();
  const [permissionError, setPermissionError] = useState<boolean>(false);
  const [permissionErrorMessage, setPermissionErrorMessage] = useState<string>("");
  const [isCheckingPermission, setIsCheckingPermission] = useState<boolean>(true);

  const [processingTime] = useMeasurementDuration();
  const [licenseKey] = useLicenseKey();
  const [isInstructionModalOpen, setIsInstructionModalOpen] = useState(true);
  const [showMeasuringModal, setShowMeasuringModal] = useState(false);
  const [showRetryModal, setShowRetryModal] = useState(false);
  
  // Check camera permissions on mount
  useEffect(() => {
    const checkCameraPermission = async () => {
      try {
        setIsCheckingPermission(true);
        await requestCameraPermission();
        setPermissionError(false);
        setIsCheckingPermission(false);
      } catch (error) {
        console.error("Camera permission error:", error);
        setPermissionError(true);
        
        if (error.error && error.error.name === 'NotAllowedError') {
          setPermissionErrorMessage("Camera access was denied. Please enable camera access in your browser settings.");
        } else if (error.error && error.error.name === 'NotFoundError') {
          setPermissionErrorMessage("No camera found on your device. Please check your hardware.");
        } else if (error.error && error.error.name === 'NotReadableError') {
          setPermissionErrorMessage("Your camera is in use by another application. Please close other apps that might be using the camera.");
        } else {
          setPermissionErrorMessage("Cannot access camera. Please ensure your device has a camera and that permissions are granted.");
        }
        
        setIsCheckingPermission(false);
      }
    };
    
    checkCameraPermission();
  }, []);
  
  const handleRetryPermission = async () => {
    setIsCheckingPermission(true);
    try {
      await requestCameraPermission();
      setPermissionError(false);
      window.location.reload(); // Full refresh to ensure permissions take effect
    } catch (error) {
      console.error("Retry camera permission error:", error);
      // Keep the error state
      setIsCheckingPermission(false);
    }
  };

  const {
    sessionState,
    vitalSigns,
    offlineMeasurements,
    error,
    warning,
    info,
    showModal,
  } = useMonitor(
    video,
    cameraId,
    processingTime,
    licenseKey,
    null,
    startMeasuring
  );

  const prevSessionState = usePrevious(sessionState);
  const errorMessage = useError(error);
  const warningMessage = useWarning(warning);
  const navigate = useNavigate();
  const isMeasuring = useCallback(
    () => sessionState === SessionState.MEASURING,
    [sessionState]
  );

  const isVideoReady = useCallback(
    () => video.current?.readyState === VideoReadyState.HAVE_ENOUGH_DATA,
    []
  );

  const handleButtonClick = useCallback(() => {
    if (sessionState === SessionState.ACTIVE) {
      setStartMeasuring(true);
      setLoadingTimeoutPromise(window.setTimeout(() => processingTime * 1000));
    } else if (isMeasuring()) {
      clearTimeout(loadingTimeoutPromise);
      setStartMeasuring(false);
    }
  }, [sessionState, processingTime]);

  useEffect(() => {
     const scanIsDone = localStorage.getItem("ScanIsDone");
     if (scanIsDone === "Done") {
       localStorage.clear();
       window.location.reload();
     }  
  }, []);


  const password = localStorage.getItem("userPassword");
  useEffect(() => {
    const checkPassword = async () => {
      try {
        const response = await axios.post(
          "https://pivv-be.yhealth.me/api/validate-code",
          { code: password },
          {
            headers: {
              "x-api-key":
                "zrEjEPASSLwXksLSBrPtIHTBowGWhttrLvH00sZ96VRNPruYlePN8ZlujLCuyCoqQBbf52TLdCxHZCDAs05NbNZ4zGUDBNuOa7G88CYMvz21lHzy6FI7AXabiOPrtQOE",
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data.success) {
          localStorage.setItem("userPassword", password);
        } else {
          localStorage.clear();
          navigate("/");
        }
      } catch (error) {
        console.error("API Call Error:", error);
        localStorage.clear();
        navigate("/");
      }
    };

    checkPassword();
  }, [password, navigate]);

  useEffect(() => {
    if (isMeasuring()) {
      if (errorMessage) {
        setIsMeasurementEnabled(false);
      } else {
        setIsMeasurementEnabled(true);
      }
      !isPageVisible && setStartMeasuring(false);
    } else if (
      (sessionState === SessionState.ACTIVE ||
        sessionState === SessionState.TERMINATED) &&
      errorMessage
    ) {
      setIsMeasurementEnabled(false);
    }
    if (
      sessionState === SessionState.ACTIVE &&
      prevSessionState !== sessionState
    ) {
      setStartMeasuring(false);
    }
  }, [errorMessage, sessionState, isPageVisible]);

  useEffect(() => {
    onLicenseStatus(!(error?.code in HealthMonitorCodes));
  }, [error]);

  useEffect(() => {
    const scanDone = localStorage.getItem("scanDone");
    if (scanDone === "Done") {
      localStorage.clear();
      window.location.reload();
    }
  }, []);

  const mobile = useMemo(() => isMobile(), []);
  const desktop = useMemo(() => !isTablet() && !isMobile(), []);

  useEffect(() => {
    if (showModal) {
      setIsResultReady(true);
      closeFaceScanModal();
    }
  }, [showModal]);

  // Handle retry action
  const handleRetry = () => {
    setIsResultReady(false);
    setShowRetryModal(false);
    window.location.reload();
  };

  useEffect(() => {
    console.log("errorMessage", errorMessage);
    console.log("warningMessage", warningMessage);
    if (warningMessage || errorMessage) {
      setShowMeasuringModal(false);
      setShowRetryModal(true); // Show retry modal on error or warning
    }
  }, [warningMessage, errorMessage]);

  const handleContinue = () => {
    navigate("/pan-card");
  };
  const closeInstructionModal = () => {

   
    setIsInstructionModalOpen(false);
    handleButtonClick();
    setShowMeasuringModal(true);
  };
  const closeFaceScanModal = () => {
    setShowMeasuringModal(false);
  };

  return (
    <>
      {isCheckingPermission && (
        <PermissionErrorOverlay>
          <Loader />
          <PermissionMessage>Checking camera permissions...</PermissionMessage>
        </PermissionErrorOverlay>
      )}
    
      {permissionError && !isCheckingPermission && (
        <PermissionErrorOverlay>
          <PermissionIcon>📷</PermissionIcon>
          <PermissionTitle>Camera Access Required</PermissionTitle>
          <PermissionMessage>{permissionErrorMessage}</PermissionMessage>
          <RetryButton onClick={handleRetryPermission}>
            Try Again
          </RetryButton>
        </PermissionErrorOverlay>
      )}
      
      {!permissionError && !isCheckingPermission && !showRetryModal && !showModal && (
        <MonitorWrapper isSettingsOpen={isSettingsOpen}>
          {/* <BackButton src={BackIcon} alt="Back" onClick={handleBackClick} />
          <HelpButton src={HelpIcon} alt="Help" onClick={handleHelpClick} /> */}

          <MeasurementContentWrapper isMobile={mobile}>
            {/* Display PAN Card information here */}

            <VideoAndStatsWrapper isMobile={mobile}>
              <VideoWrapper>
                {isMeasuring() ? (
                  <Img
                    src={info.message ? MaskError : Mask}
                    alt={info.message ? "Error Mask" : "Success Mask"}
                    isDesktop={desktop}
                  />
                ) : (
                  <>
                    {isVideoReady() && <Img src={Mask} isDesktop={desktop} />}
                  </>
                )}

                <Video ref={video} id="video" muted={true} playsInline={true} />
              </VideoWrapper>
              <ErrorAlert message={errorMessage} />
              {isMeasuring() && <WarningAlert message={warningMessage} />}
              {!isVideoReady() && <Loader />}
            </VideoAndStatsWrapper>
          </MeasurementContentWrapper>
        </MonitorWrapper>
      )}

      {isInstructionModalOpen && isVideoReady() && (
        <BottomInstructionModal
          isOpen={isInstructionModalOpen}
          onClose={closeInstructionModal}
        />
      )}
      {showMeasuringModal && (
        <FaceScanModal
          isOpen={showMeasuringModal}
          onClose={closeFaceScanModal}
          onMessage={info.message}
          isMeasuring={isMeasuring()}
        />
      )}

      {isResultReady && (
        <ResultPage vitalSigns={vitalSigns} onContinue={handleContinue} />
      )}

      <RetryModal isOpen={showRetryModal} onRetry={handleRetry} />
    </>
  );
};

export default BinahMonitor;
